/**
 * ModuleMatrix — the module/action permission matrix, extracted from
 * PersonasPage.tsx (ActionToggles + ModuleRow + their helpers) and
 * parameterized by a `ModuleMatrixScope` so the SAME component renders a
 * GROUP's own permissions and a PERSONA's permissions (own + inherited).
 *
 * THE THREE PILL STATES (see ActionPill below):
 *   own       -> bg-primary text-primary-foreground border-primary        (ships today, unchanged)
 *   inherited -> bg-primary/15 text-primary border-primary/40 dashed+Lock (NEW — granted by the persona's group)
 *   off       -> bg-muted text-muted-foreground border-border            (ships today, unchanged)
 * None rely on color alone: the inherited state also swaps the Eye/Pencil
 * glyph for Lock and is `disabled` with an explanatory title/aria-label.
 * `border-dashed` is this codebase's existing signal for "special / not
 * directly owned" (borrowed from the stale-default-tab control below) —
 * amber is taken by default-tab, blue by expanded-by-default, green by
 * active, so plum + dashed is the only combination left unclaimed.
 *
 * Sentinels (defaultTab, collapsedByDefault) are NOT permissions and are
 * NEVER inherited — they render in a visually fenced-off section (a
 * divider), persona-scope only, exactly as before.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Eye, GripVertical, Lock, Pencil, ToggleLeft, ToggleRight } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { catalogActions, isModuleId, FEATURE_CATALOG, type ModuleId } from "../../config/featureCatalog";
import type { AcAdminPersonaModule, AcDataScope } from "../graphql/adminOps";
import {
  COLLAPSED_BY_DEFAULT_ACTION,
  DEFAULT_TAB_ACTION,
  MODULE_GROUPS,
  orderResolvedRows,
  resolveDefaultTabControl,
  resolveEffectiveModules,
  type ResolvedModuleRow,
} from "../lib/personaGroupLogic";

// ---------------------------------------------------------------------------
// Read vs write classification — drives the pill's icon so the distinction is
// legible without relying on color alone (a11y: color-not-only). Actions are
// free-form strings; anything not a known read verb is treated as write.
// ---------------------------------------------------------------------------
const READ_VERBS = new Set(["view", "search", "read", "list", "get"]);
export function isWriteAction(action: string): boolean {
  return !READ_VERBS.has(action.toLowerCase());
}

/** Canonical module display order, shared by every matrix (group or persona). */
export const MODULE_ORDER: readonly string[] = Object.keys(FEATURE_CATALOG);

// ---------------------------------------------------------------------------
// Scope — who this matrix instance belongs to.
// ---------------------------------------------------------------------------
export type ModuleMatrixScope =
  | { kind: "group"; code: string; displayName: string }
  | { kind: "persona"; code: string; group?: { code: string; displayName: string } };

export interface ModuleMatrixHandlers {
  /** Upsert (add/update) one module row for the current scope holder. */
  upsertModule: (vars: {
    moduleId: string;
    sortOrder: number;
    actions?: string[];
    dataScope?: string;
    isActive?: boolean;
  }) => Promise<unknown>;
  /** Deactivate (soft-delete) one module row for the current scope holder. */
  deactivateModule: (moduleId: string) => Promise<unknown>;
}

/** Persona-scope-only: exclusivity-aware default (landing) tab control. */
export interface DefaultTabHandlers {
  currentModuleId: string | null;
  busy: boolean;
  onToggle: (moduleId: string) => void;
}

/** Group-scope-only: inline "narrowing" confirm when an action is removed from a group. */
export interface NarrowingHandlers {
  pending: { moduleId: string; action: string | null } | null;
  impactFor: (moduleId: string, action: string | null) => { losing: number; keeping: number };
  onRequestRemove: (moduleId: string, action: string | null) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function hasScopeContent(scope: AcDataScope | null | undefined): boolean {
  return !!scope && (!!scope.requireIpaMatch || (scope.allowedLineOfBusiness?.length ?? 0) > 0);
}
function serializeScope(scope: AcDataScope | null | undefined): string | undefined {
  if (!hasScopeContent(scope)) return undefined;
  return JSON.stringify({
    requireIpaMatch: scope!.requireIpaMatch ?? false,
    ...(scope!.allowedLineOfBusiness?.length ? { allowedLineOfBusiness: scope!.allowedLineOfBusiness } : {}),
  });
}

// ---------------------------------------------------------------------------
// ActionToggles — the simple, no-inheritance chip row. Used standalone by
// CreatePersonaModal (a persona doesn't exist yet, so nothing can be
// inherited) and internally by ModuleRow for the OWN-only case.
// ---------------------------------------------------------------------------
export function ActionToggles({
  moduleId,
  actions,
  onChange,
  disabled,
}: {
  moduleId: string;
  actions: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const available = useMemo(() => {
    const set = new Set<string>();
    if (isModuleId(moduleId)) for (const a of catalogActions(moduleId)) set.add(a);
    for (const a of actions) set.add(a);
    set.delete(COLLAPSED_BY_DEFAULT_ACTION);
    set.delete(DEFAULT_TAB_ACTION);
    return Array.from(set).sort((x, y) => {
      const wx = isWriteAction(x) ? 1 : 0;
      const wy = isWriteAction(y) ? 1 : 0;
      return wx - wy || x.localeCompare(y);
    });
  }, [moduleId, actions]);

  if (available.length === 0) {
    return <span className="text-xs text-muted-foreground italic">no actions</span>;
  }

  const toggle = (a: string) => {
    if (disabled) return;
    onChange(actions.includes(a) ? actions.filter((x) => x !== a) : [...actions, a]);
  };

  return (
    <div className="flex gap-1 flex-wrap" role="group" aria-label={`${moduleId} actions`}>
      {available.map((a) => {
        const on = actions.includes(a);
        const write = isWriteAction(a);
        const Icon = write ? Pencil : Eye;
        return (
          <button
            key={a}
            type="button"
            disabled={disabled}
            onClick={() => toggle(a)}
            aria-pressed={on}
            title={`${on ? "Granted" : "Not granted"} — ${a} (${write ? "write" : "read"})`}
            className={
              "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed " +
              (on
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted text-muted-foreground border-border hover:bg-accent")
            }
          >
            <Icon className="w-3 h-3" aria-hidden="true" />
            {a}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResolvedActionPills — the three-state pill row for a resolved matrix row
// (own / inherited / off), plus the sentinel section (persona scope only,
// fenced off with a divider, never inherited).
// ---------------------------------------------------------------------------
function ResolvedActionPills({
  row,
  scope,
  groupDisplayName,
  onToggleOwnAction,
  sentinelHandlers,
}: {
  row: ResolvedModuleRow;
  scope: ModuleMatrixScope;
  groupDisplayName?: string;
  onToggleOwnAction: (action: string, nextOn: boolean) => void;
  sentinelHandlers?: {
    onToggleDefaultTab?: (moduleId: string) => void;
    defaultTabState?: { isDefault: boolean; stale: boolean; busy: boolean };
    isCollapsibleParent: boolean;
    onToggleCollapsedByDefault: (moduleId: string) => void;
  };
}) {
  const cat = isModuleId(row.moduleId) ? FEATURE_CATALOG[row.moduleId as ModuleId] : undefined;
  const catalogVerbs = cat?.actions ?? [];
  const vocab = useMemo(() => {
    const set = new Set<string>([...catalogVerbs, ...row.inherited, ...row.own]);
    return Array.from(set).sort((x, y) => {
      const wx = isWriteAction(x) ? 1 : 0;
      const wy = isWriteAction(y) ? 1 : 0;
      return wx - wy || x.localeCompare(y);
    });
  }, [catalogVerbs, row.inherited, row.own]);

  return (
    <div className="flex items-center gap-1 flex-wrap min-w-0">
      {vocab.length === 0 ? (
        <span className="text-xs text-muted-foreground italic">all-or-nothing</span>
      ) : (
        vocab.map((a) => {
          const inherited = row.inherited.includes(a);
          const own = row.own.includes(a);
          const write = isWriteAction(a);
          if (inherited) {
            const Icon = Lock;
            return (
              <span
                key={a}
                title={`Granted by ${groupDisplayName ?? "the group"} — edit the group to change`}
                aria-label={`${a}, inherited from ${groupDisplayName ?? "the group"}, locked`}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium border border-dashed bg-primary/[0.15] text-primary border-primary/40 cursor-not-allowed"
              >
                <Icon className="w-3 h-3" aria-hidden="true" />
                {a}
              </span>
            );
          }
          const Icon = write ? Pencil : Eye;
          return (
            <button
              key={a}
              type="button"
              onClick={() => onToggleOwnAction(a, !own)}
              aria-pressed={own}
              title={`${own ? "Granted" : "Not granted"} — ${a} (${write ? "write" : "read"})`}
              className={
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium border transition-colors " +
                (own
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:bg-accent")
              }
            >
              <Icon className="w-3 h-3" aria-hidden="true" />
              {a}
            </button>
          );
        })
      )}

      {/* Data-scope tags — the group's restriction is a locked baseline; the
          holder's own scope (if narrower) renders alongside it. */}
      {hasScopeContent(row.groupScope) && (
        <span
          title="Group restriction — a member may narrow this further, never relax it"
          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-dashed border-primary/40 bg-primary/[0.07] text-primary"
        >
          <Lock className="w-3 h-3" aria-hidden="true" />
          {[row.groupScope!.requireIpaMatch ? "IPA match" : null, row.groupScope!.allowedLineOfBusiness?.join(", ")]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}
      {hasScopeContent(row.ownScope) && (
        <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground">
          {[row.ownScope!.requireIpaMatch ? "IPA match" : null, row.ownScope!.allowedLineOfBusiness?.join(", ")]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}

      {/* Sentinels — presentation, never permissions, never inherited. Fenced
          off from the verb pills with a divider. Persona scope only. */}
      {scope.kind === "persona" && sentinelHandlers && (
        <>
          <span className="w-px self-stretch bg-border mx-1" aria-hidden="true" />
          {sentinelHandlers.defaultTabState && (
            <button
              type="button"
              disabled={sentinelHandlers.defaultTabState.busy}
              onClick={() => sentinelHandlers.onToggleDefaultTab?.(row.moduleId)}
              aria-pressed={sentinelHandlers.defaultTabState.isDefault}
              title={
                sentinelHandlers.defaultTabState.stale
                  ? "Stale default tab — this module is inactive or a Home card; click to clear it"
                  : sentinelHandlers.defaultTabState.isDefault
                    ? "Default landing tab for this persona — click to clear"
                    : "Set as this persona's default landing tab"
              }
              className={
                "inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-xs font-medium transition-colors disabled:opacity-40 " +
                (sentinelHandlers.defaultTabState.stale
                  ? "text-amber-700 dark:text-amber-500 border border-dashed border-amber-500/50 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  : sentinelHandlers.defaultTabState.isDefault
                    ? "text-amber-600 dark:text-amber-400 hover:bg-accent"
                    : "text-muted-foreground hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-600")
              }
            >
              {sentinelHandlers.defaultTabState.isDefault ? (
                <ToggleRight className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ToggleLeft className="w-4 h-4" aria-hidden="true" />
              )}
              <span className="whitespace-nowrap">
                {sentinelHandlers.defaultTabState.stale ? "Stale default" : "Default tab"}
              </span>
            </button>
          )}
          {row.moduleId === "HomeDemographics" && (
            <button
              type="button"
              onClick={() => sentinelHandlers.onToggleCollapsedByDefault(row.moduleId)}
              aria-pressed={!row.sentinels.includes(COLLAPSED_BY_DEFAULT_ACTION)}
              title={
                row.sentinels.includes(COLLAPSED_BY_DEFAULT_ACTION)
                  ? "Card collapsed by default"
                  : "Card expanded by default"
              }
              className={
                "inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-xs font-medium transition-colors " +
                (!row.sentinels.includes(COLLAPSED_BY_DEFAULT_ACTION)
                  ? "text-blue-600 dark:text-blue-400 hover:bg-accent"
                  : "text-muted-foreground hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600")
              }
            >
              {!row.sentinels.includes(COLLAPSED_BY_DEFAULT_ACTION) ? (
                <ToggleRight className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ToggleLeft className="w-4 h-4" aria-hidden="true" />
              )}
              <span className="whitespace-nowrap">Expanded by default</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag handle plumbing — identical to the pre-extraction version.
// ---------------------------------------------------------------------------
interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

function SortableRow({ id, children }: { id: string; children: (handle: DragHandleProps) => ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: CSSProperties = {
    transform: transform ? CSS.Transform.toString({ ...transform, x: 0 }) : undefined,
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, setActivatorNodeRef })}
    </div>
  );
}

function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

// ---------------------------------------------------------------------------
// ModuleRow — one row in the matrix (own, wholly-inherited, or mixed).
// ---------------------------------------------------------------------------
function ModuleRow({
  scope,
  row,
  groupDisplayName,
  onEdit,
  onToggleAction,
  onToggleActive,
  onToggleSentinel,
  onSetDefaultTab,
  defaultTabState,
  dragHandle,
  collapsible,
  narrowing,
  busy,
}: {
  scope: ModuleMatrixScope;
  row: ResolvedModuleRow;
  groupDisplayName?: string;
  onEdit?: () => void;
  onToggleAction: (moduleId: string, action: string, nextOn: boolean) => void;
  onToggleActive: (moduleId: string) => void;
  onToggleSentinel: (moduleId: string, sentinel: string) => void;
  onSetDefaultTab?: (moduleId: string) => void;
  defaultTabState?: { isDefault: boolean; stale: boolean; busy: boolean };
  dragHandle?: DragHandleProps;
  collapsible?: { expanded: boolean; onToggle: () => void; count: number };
  narrowing?: NarrowingHandlers;
  busy?: boolean;
}) {
  const cat = isModuleId(row.moduleId) ? FEATURE_CATALOG[row.moduleId as ModuleId] : undefined;
  const label = cat?.label ?? row.moduleId;
  // A row is wholly inherited (module-level lock) only in persona scope with
  // no own row at all — its activation belongs to the group, so it shows a
  // group chip instead of the active/inactive toggle, and no drag handle.
  const whollyInherited = scope.kind === "persona" && row.fromGroup && !row.ownModule;

  const showNarrowConfirm =
    scope.kind === "group" &&
    narrowing?.pending &&
    narrowing.pending.moduleId === row.moduleId;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={
          "flex items-center gap-3 rounded-lg border bg-card px-3 py-2 transition-opacity " +
          (whollyInherited ? "border-primary/35 border-dashed " : row.isActive ? "border-border " : "border-border/50 opacity-60 ")
        }
      >
        {whollyInherited || !dragHandle ? (
          <span className="w-5 shrink-0" aria-hidden="true" />
        ) : (
          <button
            type="button"
            ref={dragHandle.setActivatorNodeRef}
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            aria-label={`Drag to reorder ${row.moduleId}`}
            className="shrink-0 p-1 rounded text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}

        {collapsible && (
          <button
            type="button"
            onClick={collapsible.onToggle}
            aria-label={collapsible.expanded ? `Collapse ${row.moduleId} sub-modules` : `Expand ${row.moduleId} sub-modules`}
            className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {collapsible.expanded ? (
              <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            )}
          </button>
        )}

        <span className={"font-mono text-sm font-semibold shrink-0 " + (row.isActive ? "" : "line-through text-muted-foreground")}>
          {label}
          {collapsible && !collapsible.expanded && collapsible.count > 0 && (
            <span className="ml-2 font-sans font-normal text-xs text-muted-foreground">
              ({collapsible.count} sub-module{collapsible.count === 1 ? "" : "s"})
            </span>
          )}
        </span>

        <div className="flex-1 min-w-0">
          <ResolvedActionPills
            row={row}
            scope={scope}
            groupDisplayName={groupDisplayName}
            onToggleOwnAction={(action, nextOn) => {
              if (scope.kind === "group" && !nextOn) {
                narrowing?.onRequestRemove(row.moduleId, action);
                return;
              }
              onToggleAction(row.moduleId, action, nextOn);
            }}
            sentinelHandlers={
              scope.kind === "persona"
                ? {
                    onToggleDefaultTab: onSetDefaultTab,
                    defaultTabState,
                    isCollapsibleParent: !!collapsible,
                    onToggleCollapsedByDefault: (moduleId) => onToggleSentinel(moduleId, COLLAPSED_BY_DEFAULT_ACTION),
                  }
                : undefined
            }
          />
        </div>

        {onEdit && !whollyInherited && (
          <button
            type="button"
            onClick={onEdit}
            title="Edit module"
            aria-label={`Edit ${row.moduleId} module`}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}

        {whollyInherited ? (
          <span
            title="This module is granted by the group and cannot be removed here"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold bg-primary/10 text-primary border border-dashed border-primary/45 shrink-0"
          >
            <Lock className="w-3 h-3" aria-hidden="true" />
            {groupDisplayName}
          </span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggleActive(row.moduleId)}
            title={row.isActive ? "Deactivate module" : "Re-activate module"}
            className={
              "p-1 rounded transition-colors disabled:opacity-40 shrink-0 " +
              (row.isActive
                ? "text-green-600 dark:text-green-400 hover:bg-destructive/10 hover:text-destructive"
                : "text-muted-foreground hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600")
            }
          >
            {row.isActive ? (
              <ToggleRight className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ToggleLeft className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {showNarrowConfirm && narrowing && (
        <NarrowConfirm
          moduleId={row.moduleId}
          action={narrowing.pending!.action}
          impact={narrowing.impactFor(row.moduleId, narrowing.pending!.action)}
          onCancel={narrowing.onCancel}
          onConfirm={narrowing.onConfirm}
        />
      )}
    </div>
  );
}

// Inline confirm — narrowing a group grant. Not a modal: it belongs to the
// row that triggered it.
function NarrowConfirm({
  moduleId,
  action,
  impact,
  onCancel,
  onConfirm,
}: {
  moduleId: string;
  action: string | null;
  impact: { losing: number; keeping: number };
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const what = action ? (
    <>
      <b className="font-mono font-semibold">{action}</b> on <b className="font-mono font-semibold">{moduleId}</b>
    </>
  ) : (
    <>
      all access to <b className="font-mono font-semibold">{moduleId}</b>
    </>
  );
  return (
    <div className="flex items-center gap-3 flex-wrap rounded-lg border border-amber-500/45 bg-amber-500/[0.08] px-3 py-2 text-sm">
      <span className="flex-1 min-w-[220px]">
        Removes {what} from <b className="tabular-nums">{impact.losing}</b> persona{impact.losing === 1 ? "" : "s"}
        {impact.keeping > 0 && (
          <>
            {" "}
            (<b className="tabular-nums">{impact.keeping}</b> keep{impact.keeping === 1 ? "s" : ""} it — granted directly)
          </>
        )}
        .
      </span>
      <button type="button" onClick={onCancel} className="px-2.5 py-1 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors">
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        className="px-2.5 py-1 rounded-lg border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive/10 transition-colors"
      >
        Remove
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopEntry — a top-level list entry is either a standalone module or an
// entire module group (parent + children per MODULE_GROUPS), dragging as one
// unit. Only rows the scope holder actually OWNS participate in drag/drop —
// a wholly-inherited row (persona scope, no own row) has no sortOrder of its
// own to reorder, so it renders in a separate, non-draggable "Inherited from
// the group" section ahead of the holder's own (possibly mixed) rows. This
// keeps the extraction behaviour-preserving for the existing persona reorder
// mechanics while still surfacing MIXED rows (inherited verb + own verb on
// the same moduleId) inline in the main, draggable list.
// ---------------------------------------------------------------------------
type TopEntry =
  | { kind: "module"; id: string; row: ResolvedModuleRow }
  | { kind: "group"; id: string; parent: ResolvedModuleRow; children: ResolvedModuleRow[] };

export interface ModuleMatrixProps {
  scope: ModuleMatrixScope;
  ownModules: AcAdminPersonaModule[];
  /** Only meaningful when scope.kind === "persona" && scope.group is set. */
  groupModules?: AcAdminPersonaModule[] | null;
  handlers: ModuleMatrixHandlers;
  onRefresh: () => void;
  onEditModule?: (mod: AcAdminPersonaModule) => void;
  defaultTab?: DefaultTabHandlers;
  narrowing?: NarrowingHandlers;
  /**
   * Cross-write serialization (reorder ⇄ default-tab), restored across the
   * ModuleMatrix/PersonaRow extraction boundary. Both a reorder batch and a
   * default-tab exclusivity write upsert a module using ITS OWN sortOrder/
   * actions read at call time; if one's mutations are still in flight when
   * the other starts, the second can carry a stale value and clobber what
   * the first just persisted. ModuleMatrix reports when ITS reorder batch is
   * in flight so the caller (which owns default-tab) can refuse to start
   * while it runs; symmetrically, ModuleMatrix refuses to start a reorder
   * while `defaultTab.busy` is true (the caller ORs its own write's busy
   * flag with this signal — see PersonaRow.tsx).
   */
  onReorderBusyChange?: (busy: boolean) => void;
}

export default function ModuleMatrix({
  scope,
  ownModules,
  groupModules,
  handlers,
  onRefresh,
  onEditModule,
  defaultTab,
  narrowing,
  onReorderBusyChange,
}: ModuleMatrixProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [busyModuleId, setBusyModuleId] = useState<string | null>(null);
  const [topOrderOverride, setTopOrderOverride] = useState<string[] | null>(null);
  const [childOrderOverrides, setChildOrderOverrides] = useState<Record<string, string[]>>({});

  const groupDisplayName = scope.kind === "persona" ? scope.group?.displayName : undefined;

  const allRows = useMemo(
    () => orderResolvedRows(resolveEffectiveModules(ownModules, scope.kind === "persona" ? groupModules : null), MODULE_ORDER),
    [ownModules, groupModules, scope.kind],
  );

  const inheritedOnlyRows = allRows.filter((r) => scope.kind === "persona" && r.fromGroup && !r.ownModule);
  const ownRows = allRows.filter((r) => !(scope.kind === "persona" && r.fromGroup && !r.ownModule));

  const groupParentIds = useMemo(() => new Set(MODULE_GROUPS.map((g) => g.parentId)), []);
  const activeGroups = useMemo(() => {
    const groups = new Map<string, { parentId: string; parent: ResolvedModuleRow; children: ResolvedModuleRow[] }>();
    for (const cfg of MODULE_GROUPS) {
      const parent = ownRows.find((r) => r.moduleId === cfg.parentId);
      if (parent) groups.set(cfg.parentId, { parentId: cfg.parentId, parent, children: [] });
    }
    for (const row of ownRows) {
      if (groupParentIds.has(row.moduleId)) continue;
      for (const cfg of MODULE_GROUPS) {
        if (!cfg.matchChild(row.moduleId)) continue;
        const group = groups.get(cfg.parentId);
        if (group) {
          group.children.push(row);
          break;
        }
      }
    }
    for (const group of groups.values()) {
      group.children.sort(
        (a, b) => (a.ownModule?.sortOrder ?? 0) - (b.ownModule?.sortOrder ?? 0),
      );
    }
    return groups;
  }, [ownRows, groupParentIds]);

  const topEntries: TopEntry[] = useMemo(() => {
    const childIds = new Set<string>();
    for (const g of activeGroups.values()) for (const c of g.children) childIds.add(c.moduleId);
    const entries: TopEntry[] = [];
    for (const row of ownRows) {
      const group = activeGroups.get(row.moduleId);
      if (group) {
        entries.push({ kind: "group", id: group.parentId, parent: group.parent, children: group.children });
        continue;
      }
      if (childIds.has(row.moduleId)) continue;
      entries.push({ kind: "module", id: row.moduleId, row });
    }
    entries.sort((a, b) => {
      const sa = a.kind === "module" ? (a.row.ownModule?.sortOrder ?? 0) : (a.parent.ownModule?.sortOrder ?? 0);
      const sb = b.kind === "module" ? (b.row.ownModule?.sortOrder ?? 0) : (b.parent.ownModule?.sortOrder ?? 0);
      return sa - sb;
    });
    return entries;
  }, [ownRows, activeGroups]);

  const naturalTopIds = topEntries.map((e) => e.id);
  const naturalChildIdsByGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const g of activeGroups.values()) map.set(g.parentId, g.children.map((c) => c.moduleId));
    return map;
  }, [activeGroups]);

  const effectiveTopIds =
    topOrderOverride && sameIdSet(topOrderOverride, naturalTopIds) ? topOrderOverride : naturalTopIds;
  const effectiveChildIds = (parentId: string): string[] => {
    const natural = naturalChildIdsByGroup.get(parentId) ?? [];
    const override = childOrderOverrides[parentId];
    return override && sameIdSet(override, natural) ? override : natural;
  };

  const topEntryById = useMemo(() => new Map(topEntries.map((e) => [e.id, e] as const)), [topEntries]);
  const rowById = useMemo(() => new Map(ownRows.map((r) => [r.moduleId, r] as const)), [ownRows]);

  const topSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const childSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const upsertRow = async (row: ResolvedModuleRow, patch: Partial<{ actions: string[]; isActive: boolean }>) => {
    const own = row.ownModule;
    const baseActions = own?.actions ?? [];
    const baseScope = own?.dataScope ?? null;
    setBusyModuleId(row.moduleId);
    try {
      await handlers.upsertModule({
        moduleId: row.moduleId,
        sortOrder: own?.sortOrder ?? ownModules.reduce((m, x) => Math.max(m, x.sortOrder), -1) + 1,
        actions: patch.actions ?? baseActions,
        dataScope: serializeScope(baseScope),
        isActive: patch.isActive ?? own?.isActive ?? true,
      });
    } finally {
      setBusyModuleId(null);
      onRefresh();
    }
  };

  const handleToggleAction = (moduleId: string, action: string, nextOn: boolean) => {
    const row = rowById.get(moduleId) ?? { moduleId, own: [], ownModule: null } as unknown as ResolvedModuleRow;
    const current = row.ownModule?.actions ?? [];
    const next = nextOn ? [...current, action] : current.filter((a) => a !== action);
    void upsertRow(row, { actions: next });
  };

  const handleToggleActive = (moduleId: string) => {
    const row = rowById.get(moduleId);
    if (!row) return;
    if (row.isActive && row.ownModule) {
      setBusyModuleId(moduleId);
      void handlers.deactivateModule(moduleId).finally(() => {
        setBusyModuleId(null);
        onRefresh();
      });
    } else {
      void upsertRow(row, { isActive: true });
    }
  };

  const handleToggleSentinel = (moduleId: string, sentinel: string) => {
    const row = rowById.get(moduleId);
    const current = row?.ownModule?.actions ?? [];
    const on = current.includes(sentinel);
    const next = on ? current.filter((a) => a !== sentinel) : [...current, sentinel];
    void upsertRow(row ?? ({ moduleId, ownModule: null } as unknown as ResolvedModuleRow), { actions: next });
  };

  // resolveDefaultTabControl gates out wholly-inherited rows (fromGroup, no
  // own row) entirely — sentinels are own-only, so such a row can never
  // legitimately hold DEFAULT_TAB_ACTION, and rendering a clickable control
  // on it would call computeDefaultTabUpserts (which only walks the
  // persona's OWN modules) and silently clear the real default without ever
  // setting the clicked one.
  const defaultTabStateFor = (row: ResolvedModuleRow) => {
    if (!defaultTab) return undefined;
    const control = resolveDefaultTabControl(row);
    if (!control) return undefined;
    return { ...control, busy: defaultTab.busy };
  };

  const persistReorder = async (topIds: string[], childOrders: Record<string, string[]>) => {
    const flatIds: string[] = [];
    for (const id of topIds) {
      flatIds.push(id);
      const group = activeGroups.get(id);
      if (group) {
        const natural = naturalChildIdsByGroup.get(id) ?? [];
        const supplied = childOrders[id];
        flatIds.push(...(supplied && sameIdSet(supplied, natural) ? supplied : natural));
      }
    }
    const byId = new Map(ownModules.map((m) => [m.moduleId, m]));
    const mutations = flatIds
      .map((id, index) => {
        const mod = byId.get(id);
        if (!mod || mod.sortOrder === index) return null;
        return handlers.upsertModule({
          moduleId: id,
          sortOrder: index,
          actions: mod.actions ?? undefined,
          dataScope: serializeScope(mod.dataScope),
          isActive: mod.isActive,
        });
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);
    if (mutations.length === 0) return;
    // Reject if a default-tab write started concurrently — see the
    // onReorderBusyChange doc comment above.
    onReorderBusyChange?.(true);
    try {
      await Promise.allSettled(mutations);
    } finally {
      onReorderBusyChange?.(false);
    }
    onRefresh();
  };

  const handleTopDragEnd = (event: DragEndEvent) => {
    // Refuse to start while a default-tab write is in flight — see the
    // onReorderBusyChange doc comment above.
    if (defaultTab?.busy) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = effectiveTopIds.indexOf(String(active.id));
    const newIndex = effectiveTopIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(effectiveTopIds, oldIndex, newIndex);
    setTopOrderOverride(next);
    void persistReorder(next, childOrderOverrides);
  };

  const handleChildDragEnd = (parentId: string, event: DragEndEvent) => {
    // Refuse to start while a default-tab write is in flight (same reason as
    // handleTopDragEnd above).
    if (defaultTab?.busy) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = effectiveChildIds(parentId);
    const oldIndex = current.indexOf(String(active.id));
    const newIndex = current.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(current, oldIndex, newIndex);
    const nextOverrides = { ...childOrderOverrides, [parentId]: next };
    setChildOrderOverrides(nextOverrides);
    void persistReorder(effectiveTopIds, nextOverrides);
  };

  const renderRow = (row: ResolvedModuleRow, handle: DragHandleProps | undefined, collapsible?: { expanded: boolean; onToggle: () => void; count: number }) => (
    <ModuleRow
      scope={scope}
      row={row}
      groupDisplayName={groupDisplayName}
      onEdit={onEditModule && row.ownModule ? () => onEditModule(row.ownModule!) : undefined}
      onToggleAction={handleToggleAction}
      onToggleActive={handleToggleActive}
      onToggleSentinel={handleToggleSentinel}
      onSetDefaultTab={defaultTab ? (id) => defaultTab.onToggle(id) : undefined}
      defaultTabState={defaultTabStateFor(row)}
      dragHandle={handle}
      collapsible={collapsible}
      narrowing={narrowing}
      busy={busyModuleId === row.moduleId}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      {inheritedOnlyRows.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-1">
          <p className="text-xs font-medium text-muted-foreground">
            Inherited from <span className="font-semibold text-foreground">{groupDisplayName}</span>
          </p>
          {inheritedOnlyRows.map((row) => (
            <div key={row.moduleId}>{renderRow(row, undefined)}</div>
          ))}
        </div>
      )}

      {ownRows.length === 0 && inheritedOnlyRows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No modules configured.</p>
      ) : ownRows.length > 0 ? (
        <DndContext sensors={topSensors} collisionDetection={closestCenter} onDragEnd={handleTopDragEnd}>
          <SortableContext items={effectiveTopIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {effectiveTopIds.map((id) => {
                const entry = topEntryById.get(id);
                if (!entry) return null;
                return (
                  <SortableRow key={id} id={id}>
                    {(handle) =>
                      entry.kind === "module" ? (
                        renderRow(entry.row, handle)
                      ) : (
                        <div>
                          {renderRow(entry.parent, handle, {
                            expanded: expandedGroups.has(entry.id),
                            onToggle: () =>
                              setExpandedGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(entry.id)) next.delete(entry.id);
                                else next.add(entry.id);
                                return next;
                              }),
                            count: entry.children.length,
                          })}
                          {expandedGroups.has(entry.id) && entry.children.length > 0 && (
                            <div className="mt-2 ml-6 pl-3 border-l-2 border-border/50 space-y-2">
                              <DndContext
                                sensors={childSensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(e) => handleChildDragEnd(entry.id, e)}
                              >
                                <SortableContext items={effectiveChildIds(entry.id)} strategy={verticalListSortingStrategy}>
                                  {effectiveChildIds(entry.id).map((cid) => {
                                    const crow = rowById.get(cid);
                                    if (!crow) return null;
                                    return (
                                      <SortableRow key={cid} id={cid}>
                                        {(childHandle) => renderRow(crow, childHandle)}
                                      </SortableRow>
                                    );
                                  })}
                                </SortableContext>
                              </DndContext>
                            </div>
                          )}
                        </div>
                      )
                    }
                  </SortableRow>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : null}
    </div>
  );
}
